import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { getNavSettings, saveNavSettings } from "@/lib/api/settings.functions";
import { listServiceCategoriesAdmin } from "@/lib/api/services.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { FormActions } from "@/components/FormActions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { SortableList } from "@/components/admin/SortableList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import type { NavItem, NavItemType } from "@/lib/navigation/types";
import { useAdminUser } from "@/lib/auth/useAdminUser";
import { canManageMenuStructure } from "@/lib/auth/permissions";

export const Route = createFileRoute("/admin/menus/")({
  loader: async () => {
    const [nav, categories] = await Promise.all([getNavSettings(), listServiceCategoriesAdmin()]);
    return {
      ...nav,
      serviceCategories: categories.map((c) => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
        status: c.status,
      })),
    };
  },
  component: AdminMenusPage,
});

type ServiceCategoryOption = {
  id: string;
  name: string;
  slug: string;
  status: string;
};

function FooterServicesEditor({
  slugs,
  onChange,
  categories,
  allowEdits,
}: {
  slugs: string[];
  onChange: (slugs: string[]) => void;
  categories: ServiceCategoryOption[];
  allowEdits: boolean;
}) {
  const [selectedSlug, setSelectedSlug] = useState("");

  const categoryBySlug = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.slug, c])),
    [categories],
  );

  const available = categories.filter(
    (c) => c.status === "published" && !slugs.includes(c.slug),
  );

  function addSelected() {
    if (!selectedSlug || slugs.includes(selectedSlug)) return;
    onChange([...slugs, selectedSlug]);
    setSelectedSlug("");
  }

  function removeSlug(slug: string) {
    onChange(slugs.filter((s) => s !== slug));
  }

  return (
    <div className="space-y-4">
      {allowEdits ? (
        <FormActions className="w-full max-w-lg">
          <Select
            value={selectedSlug || undefined}
            onValueChange={setSelectedSlug}
            disabled={available.length === 0}
          >
            <SelectTrigger className="w-full max-w-sm">
              <SelectValue placeholder={available.length ? "Select a service…" : "All services added"} />
            </SelectTrigger>
            <SelectContent>
              {available.map((c) => (
                <SelectItem key={c.id} value={c.slug}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={addSelected} disabled={!selectedSlug}>
            <Plus className="mr-2 h-4 w-4" /> Add
          </Button>
        </FormActions>
      ) : null}

      {slugs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No services selected for the footer.</p>
      ) : (
        <SortableList
          items={slugs}
          getId={(slug) => slug}
          onReorder={onChange}
          disabled={!allowEdits}
          renderItem={(slug) => {
            const category = categoryBySlug[slug];
            return (
              <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {category?.name ?? slug}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    /services/{slug}
                    {!category ? " · not found" : category.status !== "published" ? " · not published" : ""}
                  </p>
                </div>
                {allowEdits ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${category?.name ?? slug}`}
                    onClick={() => removeSlug(slug)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                ) : null}
              </div>
            );
          }}
        />
      )}
    </div>
  );
}

const NAV_TYPES: { value: NavItemType; label: string }[] = [
  { value: "link", label: "Link" },
  { value: "mega_services", label: "Mega menu — Services" },
  { value: "mega_insights", label: "Mega menu — Insights" },
  { value: "mega_stories", label: "Mega menu — Client Stories" },
];

function newNavItem(): NavItem {
  return {
    id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: "New item",
    type: "link",
    href: "/",
    visible: true,
  };
}

function MenuEditor({
  items,
  onChange,
  allowMegaTypes = false,
  allowStructureEdits = true,
}: {
  items: NavItem[];
  onChange: (items: NavItem[]) => void;
  allowMegaTypes?: boolean;
  allowStructureEdits?: boolean;
}) {
  function updateItem(index: number, patch: Partial<NavItem>) {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function removeItem(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function updateChild(sectionIndex: number, childIndex: number, patch: Partial<NavItem>) {
    const section = items[sectionIndex];
    const children = section.children ?? [];
    const nextChildren = children.map((child, i) =>
      i === childIndex ? { ...child, ...patch } : child,
    );
    updateItem(sectionIndex, { children: nextChildren });
  }

  function addChild(sectionIndex: number) {
    const section = items[sectionIndex];
    updateItem(sectionIndex, {
      children: [...(section.children ?? []), newNavItem()],
    });
  }

  function removeChild(sectionIndex: number, childIndex: number) {
    const section = items[sectionIndex];
    updateItem(sectionIndex, {
      children: (section.children ?? []).filter((_, i) => i !== childIndex),
    });
  }

  const typeOptions = allowMegaTypes
    ? NAV_TYPES
    : NAV_TYPES.filter((t) => t.value === "link");

  return (
    <div className="space-y-3">
      <SortableList
        items={items}
        getId={(item) => item.id}
        onReorder={onChange}
        renderItem={(item, index) => (
          <div className="rounded-lg border p-4">
            <div className="flex flex-wrap items-start gap-3">
              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Label</Label>
                  <Input
                    value={item.label}
                    onChange={(e) => updateItem(index, { label: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={item.type}
                    onValueChange={(value) =>
                      updateItem(index, { type: value as NavItemType })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {typeOptions.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {item.type === "link" && (
                  <div className="space-y-2 sm:col-span-2">
                    <Label>URL</Label>
                    <Input
                      value={item.href ?? ""}
                      onChange={(e) => updateItem(index, { href: e.target.value })}
                      placeholder="/about"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={item.visible}
                  onCheckedChange={(visible) => updateItem(index, { visible })}
                />
                {item.visible ? (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                )}
                {allowStructureEdits ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                ) : null}
              </div>
            </div>

            {!allowMegaTypes && (
              <div className="mt-4 space-y-2 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-muted-foreground">Child links</Label>
                  {allowStructureEdits ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => addChild(index)}>
                      <Plus className="mr-1 h-3 w-3" /> Add link
                    </Button>
                  ) : null}
                </div>
                <SortableList
                  items={item.children ?? []}
                  getId={(child) => child.id}
                  onReorder={(children) => updateItem(index, { children })}
                  renderItem={(child, childIndex) => (
                    <div className="flex flex-wrap items-end gap-2 rounded-md bg-muted/40 p-3">
                      <div className="grid flex-1 gap-2 sm:grid-cols-2">
                        <Input
                          value={child.label}
                          onChange={(e) => updateChild(index, childIndex, { label: e.target.value })}
                          placeholder="Label"
                        />
                        <Input
                          value={child.href ?? ""}
                          onChange={(e) => updateChild(index, childIndex, { href: e.target.value })}
                          placeholder="/path"
                        />
                      </div>
                      <Switch
                        checked={child.visible}
                        onCheckedChange={(visible) => updateChild(index, childIndex, { visible })}
                      />
                      {allowStructureEdits ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeChild(index, childIndex)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      ) : null}
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        )}
      />

      {allowStructureEdits ? (
        <FormActions>
          <Button type="button" variant="outline" onClick={() => onChange([...items, newNavItem()])}>
            <Plus className="mr-2 h-4 w-4" /> Add menu item
          </Button>
        </FormActions>
      ) : null}
    </div>
  );
}

function AdminMenusPage() {
  const initial = Route.useLoaderData();
  const adminUser = useAdminUser();
  const allowStructureEdits = canManageMenuStructure(adminUser.permissions);
  const [headerMenu, setHeaderMenu] = useState<NavItem[]>(initial.headerMenu);
  const [footerMenu, setFooterMenu] = useState<NavItem[]>(initial.footerMenu);
  const [footerServiceSlugs, setFooterServiceSlugs] = useState<string[]>(
    initial.footerServiceSlugs,
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await saveNavSettings({
        data: { headerMenu, footerMenu, footerServiceSlugs },
      });
      toast.success("Menus saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Menus"
        description="Configure header navigation, footer columns, and featured service links."
      />

      <Tabs defaultValue="header">
        <TabsList>
          <TabsTrigger value="header">Header</TabsTrigger>
          <TabsTrigger value="footer">Footer</TabsTrigger>
          <TabsTrigger value="services">Footer services</TabsTrigger>
        </TabsList>

        <TabsContent value="header" className="mt-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Mega menu items load live services, insights, and client stories from the CMS.
          </p>
          <MenuEditor items={headerMenu} onChange={setHeaderMenu} allowMegaTypes allowStructureEdits={allowStructureEdits} />
        </TabsContent>

        <TabsContent value="footer" className="mt-6">
          <MenuEditor items={footerMenu} onChange={setFooterMenu} allowStructureEdits={allowStructureEdits} />
        </TabsContent>

        <TabsContent value="services" className="mt-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Choose which service categories appear in the footer Services column. Drag to reorder.
          </p>
          <FooterServicesEditor
            slugs={footerServiceSlugs}
            onChange={setFooterServiceSlugs}
            categories={initial.serviceCategories}
            allowEdits={allowStructureEdits}
          />
        </TabsContent>
      </Tabs>

      <AdminFormFooter cancelTo="/admin" onSave={handleSave} saving={saving} saveLabel="Save menus" />
    </AdminPageShell>
  );
}
